/**
 * Pluralizing words, ex: duck
 * 1 duck
 * 2 ducks
 * 0 ducks
 *
 * @param count number of items
 * @param word string - base word
 * @param suffix string - how to make it plural (sometimes can be 'es')
 */
export const pluralize = (count: number, word: string, suffix = 's') => {
    return `${count} ${word}${count === 1 ? '' : suffix}`;
};

export const capitalize = (word: string) => {
    return word.charAt(0).toUpperCase() + word.slice(1);
};

export const replaceAt = (
    text: string,
    startIndex: number,
    endIndex: number,
    replacement: string
): string => {
    return text.substr(0, startIndex) + replacement + text.substr(endIndex);
};

export const flattenGetParam = (param: string | string[] | undefined): string | undefined =>
    Array.isArray(param) ? param[0] : param;

export const formattedBirthday = (text: string) => {
    const val = text.replace(/\D/g, '');
    if (val.length > 2) {
        return val.substring(0, 2) + '-' + val.substring(2);
    }
    return val;
};

export const formatExpiration = (text: string) => {
    const val = text.replace(/\D/g, '');
    if (val.length > 2) {
        return val.substring(0, 2) + '/' + val.substring(2, 4);
    }
    return val;
};

export const formatCardNumber = (input: string | undefined) => {
    // See https://just-salad-llc.sentry.io/issues/5213298468 somehow this is
    // receiving an undefined value for `input` so we might as well expected it
    // and deal accordingly. I suspect that `onBlur` can return undefined
    // instead of the empty string and the typechecker wasn't catching it, but
    // catching here is safer.
    if (input === undefined) {
        return '';
    }

    const ccNumString = input.replace(/[^0-9]/g, '');
    // mc, starts with - 51 to 55
    // v, starts with - 4
    // dsc, starts with 6011, 622126-622925, 644-649, 65
    // amex, starts with 34 or 37
    const typeCheckString = ccNumString.substring(0, 2);
    let cType = '';
    let block1 = '';
    let block2 = '';
    let block3 = '';
    let block4 = '';

    if (typeCheckString.length === 2) {
        const typeCheck = parseInt(typeCheckString);
        if (typeCheck >= 40 && typeCheck <= 49) {
            cType = 'Visa';
        } else if (typeCheck >= 51 && typeCheck <= 55) {
            cType = 'Master Card';
        } else if ((typeCheck >= 60 && typeCheck <= 62) || typeCheck === 64 || typeCheck === 65) {
            cType = 'Discover';
        } else if (typeCheck === 34 || typeCheck === 37) {
            cType = 'American Express';
        } else {
            cType = 'Invalid';
        }
    }

    // all support card types have a 4 digit firt block
    block1 = ccNumString.substring(0, 4);

    if (cType === 'American Express') {
        // for Amex cards
        block2 = ccNumString.substring(4, 10);
        block3 = ccNumString.substring(10, 15);
        block4 = '';
    } else {
        // for 4X4 cards
        block2 = ccNumString.substring(4, 8);
        block3 = ccNumString.substring(8, 12);
        block4 = ccNumString.substring(12, 16);
    }
    return [block1, block2, block3, block4].filter((block) => block !== '').join(' ');
};

export const formatPhone = (phone: string) => {
    const digits = phone.replace(/\D/g, '');
    const length = digits.length;

    // No digits → empty string, NOT the input echoed back.
    //
    // This function is the `onMask` for every phone input (PhoneNumberScreen on mobile,
    // PhoneNumberModal on web, EditAccountForm), so it decides what the field displays.
    // Returning `phone` here was the one path that let non-numeric input through: an empty
    // field plus "a" produced "a". Every other length already drops non-digits — they are
    // stripped into `digits` above — which is why the leak only showed on an EMPTY field.
    //
    // `keyboardType="number-pad"` cannot carry this on its own: it is a soft-keyboard hint,
    // so it does not constrain a hardware keyboard, web typing, paste, or autofill.
    //
    // Display-only callers (CheckoutScreen, order summaries) pass stored digit strings and
    // are unaffected.
    if (length === 0) {
        return '';
    }

    let area = digits.slice(0, 3);
    let prefix = '';
    let exchange = '';

    if (length > 3) {
        area = `(${area})`;
        prefix = digits.slice(3, 6);
    }

    if (length > 6) {
        exchange = digits.slice(6);

        return `${area} ${prefix} - ${exchange}`;
    }

    return [area, prefix].filter(Boolean).join(' ');
};

export const unformatPhone = (formatted: string) => formatted.replace(/\D/g, '');

/**
 * True when a stored phone is the all-zeroes placeholder — `(000) 000 - 0000` in any
 * formatting, or any all-zero digit string. Accounts created without a real number carry this
 * instead of an empty field, and showing it in an input reads as a genuine number the user
 * must clear before typing their own.
 *
 * Only all-zero values qualify: a real number never is one, so this cannot swallow real input.
 * An empty/undefined value is NOT a placeholder — it is already blank and needs no special
 * handling.
 */
export const isPlaceholderPhone = (phone?: string | null): boolean => {
    const digits = (phone ?? '').replace(/\D/g, '');
    return digits.length > 0 && /^0+$/.test(digits);
};

/**
 * Seeds a phone form field: formats a real number, and yields '' for the all-zeroes
 * placeholder so the input renders empty with its own placeholder text showing.
 */
export const formatPhoneForInput = (phone?: string | null): string =>
    isPlaceholderPhone(phone) ? '' : formatPhone(phone ?? '');

export const greetingCopy = (name?: string): string => {
    if (!name) {
        return 'Eat With Purpose';
    }

    const currentHour = new Date().getHours();

    if (currentHour >= 5 && currentHour <= 11) {
        return `Caesar the day, ${name}!`;
    } else if (currentHour < 16) {
        return `What's for lunch, ${name}?`;
    } else {
        return `Name your craving, ${name}.`;
    }
};

export const toHash = (str: string) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash &= hash; // Convert to 32bit integer
    }
    return new Uint32Array([hash])[0].toString(36);
};
